import { useState } from "react"
import striptags from "striptags"

import getShopifyProductsAndClasses from "../utils/getProductsAndClasses"
import swapProductsAndClasses from "../utils/swapProductsAndClasses"
import addLinkTracking from "../utils/addLinkTracking"

const useBody = (article, contentfulAd, inlineAdIndex) => {
  const { id: articleId, body, partnerIds, funnel, template } = article
  const isStandard = template === "standard"

  const { shopifyProducts, classes } = getShopifyProductsAndClasses(articleId)

  useState(() => {
    // experiment: add an additional product placement if the last element is a product
    const lastElement = body[body.length - 1]
    if (lastElement.type === "product") {
      lastElement.className = "experiment-product-placement-footer-A"

      body.push({
        ...lastElement,
        className: "experiment-product-placement-footer-B",
        useTestimonial: true,
        hidden: true,
      })
    }

    let allElements = []
    let previousElement = null
    let reviewItemSum = 0
    let productReviewQuickListElement = null
    let headingAccordionElement = null

    const somePromotionalPlacementsAreValid = body
      .filter(({ type }) => type === "promotional-placement")
      .some(({ promotionalUnit }) => Boolean(promotionalUnit))

    const countWords = (text) => text.trim().split(/\s+/).length
    const splitParagraphs = (element) => {
      const paragraphs = element.text
        .split("</p>")
        .map((html, i) => ({
          ...element,
          uuid: `${element.uuid}_${i}`,
          text: `${html}</p>`,
          wordCount: countWords(striptags(html)),
        }))
        .filter(({ text }) => text !== "</p>")

      paragraphs[paragraphs.length - 1] = { ...paragraphs[paragraphs.length - 1], end: true }

      return paragraphs
    }

    body.forEach((el, index) => {
      const element = JSON.parse(JSON.stringify(el))

      element.uuid = index // since this function only runs once index is as good as uuid

      // if we have a text element following a product element we group them together
      // for potential order swap on mobile device
      if (previousElement?.type === "product" && element.type === "text") {
        const currentElement = {
          type: "product-and-text",
          uuid: index,
          product: previousElement,
          text: element,
        }
        allElements.pop()
        allElements.push(currentElement)
        previousElement = currentElement

        return
      }

      if (element.type === "list") {
        element.wordCount = element.items.reduce(
          (acc, item) => acc + countWords(striptags(item.text)),
          0,
        )
      } else if (element.type === "heading") {
        element.wordCount = countWords(striptags(element.text))
      }

      // use start indices for review items, because they can be split into multiple components
      if (element.type === "review" && !element.quickView) {
        element.startIndex = reviewItemSum
        reviewItemSum += element.items.length
      }

      if (element.type === "product-review-quick-list") {
        element.productReviews = []
        productReviewQuickListElement = element
      }

      if (element.type === "product-review") {
        productReviewQuickListElement?.productReviews.push(element)
      }

      // initialize heading accordion element with an empty content array
      if (element.type === "heading" && element.accordion && !headingAccordionElement) {
        element.content = []
        headingAccordionElement = element
      }

      // terminate heading accordion if:
      // 1. We hit a heading-terminator OR
      // 2. We hit a heading of the same level OR
      // 3. We're at the last element
      if (
        headingAccordionElement &&
        (element.type === "heading-terminator" ||
          (element.type === "heading" &&
            element.level === headingAccordionElement.level &&
            element !== headingAccordionElement) ||
          index === body.length - 1)
      ) {
        allElements.push(headingAccordionElement)
        headingAccordionElement = null
      }

      // add elements to heading accordion content array
      if (element.type !== "ad" && element.type !== "heading" && headingAccordionElement) {
        headingAccordionElement.content.push(element)
      }

      // fallback to ad if promotional placement does not have a promotional unit
      // (if all promotional placements are invalid, skip the element)
      if (element.type === "promotional-placement" && !element.promotionalUnit) {
        element.type = somePromotionalPlacementsAreValid ? "ad" : "dummy"
      }

      if (!headingAccordionElement) {
        if (element.type === "ad") {
          const currentElement =
            contentfulAd.mobile || contentfulAd.desktop
              ? {
                  type: "contentful-ad",
                  ...contentfulAd,
                }
              : { ...element, index: inlineAdIndex.current }
          allElements.push(currentElement)
          previousElement = currentElement

          if (!(contentfulAd.mobile || contentfulAd.desktop)) {
            inlineAdIndex.current += 1
          }
        } else if (element.type === "text") {
          allElements.push(...splitParagraphs(element, index))
        } else {
          allElements.push(element)
        }
      }
    })

    const hasAdOrPromotionalPlacement = allElements.some(({ type }) =>
      ["ad", "promotional-placement"].includes(type),
    )
    if (!hasAdOrPromotionalPlacement && isStandard) {
      // we can have up to 10 ads and 2 "In Content" promotional placements,
      // and 1 "Below Content" promotional placement
      const maxPromotions = 12
      let firstPromotionAdded = false
      let lastPromotionIndex = -1
      let promotionCount = 0
      let wordCount = 0

      // if there are less than 3 components left in the body of an article,
      // we stop adding promotions, because there will always be a promotion
      // below the bottom of an article
      const noOfHiddenAmongLast3 = allElements
        .slice(allElements.length - 3)
        .filter(({ hidden }) => hidden).length

      for (let i = 0; i < allElements.length - (3 + noOfHiddenAmongLast3); i += 1) {
        const currentElement = allElements[i]
        const nextElement = allElements[i + 1]

        // reset last promotion index and word count if we encounter a product or a shop component
        if (["product", "shop"].includes(currentElement.type)) {
          lastPromotionIndex = i
          wordCount = 0
        }

        if (["text", "list", "heading"].includes(currentElement.type)) {
          wordCount += currentElement.wordCount
        }

        if (
          // do not add a promotion if there are more than max no. of promotions already
          promotionCount < maxPromotions &&
          // preferably add a promotion every 3 components
          // (or 0 if we haven't yet added the first promotion)
          i - lastPromotionIndex >= (firstPromotionAdded ? 3 : 1) &&
          // do not add a promotion if there are less than 200 words since the last promotion
          // (or 0 if we haven't yet added the first promotion)
          wordCount > (firstPromotionAdded ? 200 : 100) &&
          // do not add a promotion after
          !["heading", "product", "shop", "image", "embed", "video", "text-and-image"].includes(
            currentElement.type,
          ) &&
          // do not add a promotion before
          !(
            ["product", "shop", "image", "list", "summary", "review"].includes(nextElement.type) ||
            (nextElement.type === "heading" && nextElement.accordion)
          )
        ) {
          allElements.splice(i + 1, 0, { type: "promotion", uuid: `promotion_${promotionCount}` })
          firstPromotionAdded = true
          lastPromotionIndex = i + 1
          promotionCount += 1
          wordCount = 0
        }
      }

      // add the last promotion (will become "Below Content" promotional placement)
      const lastElement = allElements[allElements.length - 1]
      if (!["product", "shop"].includes(lastElement.type)) {
        allElements.push({ type: "promotion", uuid: `promotion_${promotionCount}` })
      }
    }

    // add funnel as the last element,
    // before promotion / ad / contentful ad / promotional placement if one exists at the end
    if (funnel) {
      const lastElement = allElements[allElements.length - 1]
      if (
        ["promotion", "ad", "contentful-ad", "promotional-placement"].includes(lastElement.type)
      ) {
        allElements.splice(allElements.length - 1, 0, funnel)
      } else {
        allElements.push(funnel)
      }
    }

    // swap products in body components for Shopify and class data
    allElements = swapProductsAndClasses(allElements, shopifyProducts, classes)

    // add in link tracking
    addLinkTracking(articleId, partnerIds, allElements)

    article.elements = allElements
  })
}

export default useBody
